// src/components/Tasks/FoldersView.jsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import { 
  HiFolder, 
  HiFolderAdd, 
  HiChevronRight, 
  HiPencil, 
  HiTrash, 
  HiX, 
  HiFolderOpen, 
  HiPlus,
  HiCheck,
  HiSearch,
  HiDuplicate
} from "react-icons/hi";
import { getCookie, getCookieJSON } from "../../services/cookie";
import { TaskCard } from "./TasksList";
import { LangContext } from "../../contexts/LangContext";
import en from "../../i18n/en";
import pl from "../../i18n/pl";

// ==================== Модалка для папок ====================
function FolderModal({ mode, folder, parentId, onClose, onSave, isMobile, t }) {
  const [name, setName] = useState(folder?.name || "");
  const [saving, setSaving] = useState(false);


  
  const handleSubmit = async () => {
    if (!name.trim()) return;
    
    try {
      setSaving(true);
      await onSave(name.trim(), parentId);
      onClose();
    } catch (err) {
      console.error("Folder save error:", err);
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 10000, padding: 16
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, width: "100%",
        maxWidth: 420, padding: 24,
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {mode === "create" ? t.createFolder : t.renameFolder}
          </h3>
          <button onClick={onClose} style={{
            padding: 8, borderRadius: 8, border: "1.5px solid var(--border)",
            background: "#fff", cursor: "pointer", color: "var(--muted)",
            display: "flex", alignItems: "center",
          }}>
            <HiX size={20} />
          </button>
        </div>
        
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
            if (e.key === "Escape") onClose();
          }}
          autoFocus
          placeholder={t.folderNamePlaceholder}
          style={{
            width: "100%", padding: "12px 14px",
            borderRadius: 10, border: "1.5px solid var(--border)",
            fontSize: 14, outline: "none", boxSizing: "border-box",
          }}
        />
        
        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            padding: "8px 16px", borderRadius: 8,
            border: "1.5px solid var(--border)", background: "#fff",
            cursor: "pointer", fontSize: 14, fontWeight: 500,
          }}>
            {t.cancel}
          </button>
          <button onClick={handleSubmit} disabled={saving || !name.trim()} style={{
            padding: "8px 16px", borderRadius: 8,
            border: "none", background: "var(--blue)",
            color: "#fff", cursor: saving ? "not-allowed" : "pointer",
            fontSize: 14, fontWeight: 600, opacity: saving ? 0.6 : 1,
          }}>
            {saving ? t.saving : t.save}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== Модалка добавления задач в папку ====================
function AddTasksToFolderModal({ folderId, folderName, onClose, onAdded, isMobile, t }) {
  const [tasks, setTasks] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const API_BASE = "https://flautreach.com/flautreach/api";
  
  useEffect(() => {
    fetchAvailableTasks();
  }, []);
  
  const fetchAvailableTasks = async () => {
    try {
      setLoading(true);
      const token = getCookie("authToken");
      const res = await fetch(`${API_BASE}/tasks/`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      
      if (!res.ok) throw new Error("Failed to fetch tasks");
      
      const data = await res.json();
      const availableTasks = Array.isArray(data) 
        ? data.filter(task => !task.folder)
        : [];
      
      setTasks(availableTasks);
    } catch (err) {
      console.error("Fetch tasks error:", err);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };
  
  const filteredTasks = tasks.filter(task => 
    task.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const toggleTask = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };
  
  const handleAdd = async () => {
    if (selectedTasks.length === 0) return;
    
    try {
      setSaving(true);
      const token = getCookie("authToken");
      
      for (const taskId of selectedTasks) {
        await fetch(`${API_BASE}/tasks/${taskId}/move-to-folder/`, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ folder_id: folderId }),
        });
      }
      
      onAdded();
      onClose();
    } catch (err) {
      console.error("Move tasks error:", err);
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 10000, padding: 16
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, width: "100%",
        maxWidth: 520, maxHeight: "80vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      }}>
        <div style={{ 
          padding: 24, borderBottom: "1.5px solid var(--border)",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
              {t.addTasksToFolder}
            </h3>
            <p style={{ margin: "4px 0 0 0", fontSize: 13, color: "var(--muted)" }}>
              {folderName}
            </p>
          </div>
          <button onClick={onClose} style={{
            padding: 8, borderRadius: 8, border: "1.5px solid var(--border)",
            background: "#fff", cursor: "pointer", color: "var(--muted)",
          }}>
            <HiX size={20} />
          </button>
        </div>
        
        <div style={{ padding: "16px 24px 8px" }}>
          <div style={{ position: "relative" }}>
            <HiSearch size={16} style={{ 
              position: "absolute", left: 12, top: "50%", 
              transform: "translateY(-50%)", color: "var(--muted)" 
            }} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t.searchTasks}
              style={{
                width: "100%", padding: "10px 14px 10px 36px",
                borderRadius: 8, border: "1.5px solid var(--border)",
                fontSize: 13, outline: "none", boxSizing: "border-box",
              }}
            />
          </div>
        </div>
        
        <div style={{ flex: 1, overflow: "auto", padding: "8px 24px 16px" }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
              <div className="spinner" />
            </div>
          ) : filteredTasks.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--muted)" }}>
              {searchTerm ? t.noTasksFound : t.noAvailableTasks}
            </div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {filteredTasks.map(task => (
                <button
                  key={task.id}
                  onClick={() => toggleTask(task.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: `1.5px solid ${selectedTasks.includes(task.id) ? "var(--blue)" : "var(--border)"}`,
                    background: selectedTasks.includes(task.id) ? "rgba(59,113,254,0.05)" : "#fff",
                    cursor: "pointer",
                    transition: ".15s",
                    textAlign: "left",
                  }}
                >
                  <div style={{
                    width: 20, height: 20, borderRadius: 6,
                    border: `2px solid ${selectedTasks.includes(task.id) ? "var(--blue)" : "#d1d5db"}`,
                    background: selectedTasks.includes(task.id) ? "var(--blue)" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    {selectedTasks.includes(task.id) && <HiCheck size={14} color="#fff" />}
                  </div>
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "var(--dark)" }}>
                      {task.name}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                      {task.task_type} · {task.status}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        
        <div style={{ 
          padding: 16, borderTop: "1.5px solid var(--border)", 
          display: "flex", gap: 8, justifyContent: "space-between",
          alignItems: "center",
        }}>
          <div style={{ fontSize: 13, color: "var(--muted)" }}>
            {selectedTasks.length} {t.selected}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} style={{
              padding: "8px 16px", borderRadius: 8,
              border: "1.5px solid var(--border)", background: "#fff",
              cursor: "pointer", fontSize: 14,
            }}>
              {t.cancel}
            </button>
            <button 
              onClick={handleAdd} 
              disabled={saving || selectedTasks.length === 0}
              style={{
                padding: "8px 20px", borderRadius: 8,
                border: "none", background: "var(--blue)",
                color: "#fff", cursor: saving || selectedTasks.length === 0 ? "not-allowed" : "pointer",
                fontSize: 14, fontWeight: 600, 
                opacity: saving || selectedTasks.length === 0 ? 0.6 : 1,
              }}
            >
              {saving ? t.adding : t.add}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== Главный компонент папок ====================
export function FoldersView({ onVisualize, onAdd, userInfo }) {
  const { lang } = React.useContext(LangContext);
  const fullT = lang === "pl" ? pl : en;
  const t = fullT.tasksListPage?.folders || fullT.folders || {};
  const tasksT = fullT.tasksListPage || {};
  
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showAddTasksModal, setShowAddTasksModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // В компоненте FoldersView добавьте состояния:
const [renameModal, setRenameModal] = useState({ open: false, taskId: null, currentName: "" });
const [newName, setNewName] = useState("");
const [renaming, setRenaming] = useState(false);
  

  const API_BASE = "https://flautreach.com/flautreach/api";
  const userInfoFromCookie = getCookieJSON('userInfo');
  const effectiveUserInfo = userInfo || userInfoFromCookie;
  
  // Сохранение текущей папки в localStorage
  const [savedFolderId, setSavedFolderId] = useState(() => {
    try {
      return localStorage.getItem('currentFolderId') || null;
    } catch {
      return null;
    }
  });
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  const fetchFolders = useCallback(async (parentId = null) => {
    try {
      setLoading(true);
      const token = getCookie("authToken");
      
      if (parentId) {
        const res = await fetch(`${API_BASE}/folders/${parentId}/`, {
          headers: { "Authorization": `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch folder");
        
        const data = await res.json();
        setCurrentFolder(data);
        setBreadcrumbs(data.breadcrumbs || []);
        setFolders(data.subfolders || []);
        
        const tasksRes = await fetch(`${API_BASE}/folders/${parentId}/tasks/`, {
          headers: { "Authorization": `Bearer ${token}` },
        });
        if (tasksRes.ok) {
          const tasksData = await tasksRes.json();
          setTasks(tasksData.tasks || []);
        }
        
        // Сохраняем текущую папку
        try {
          localStorage.setItem('currentFolderId', String(parentId));
        } catch (err) {
          console.error("Failed to save folder:", err);
        }
      } else {
        const res = await fetch(`${API_BASE}/folders/`, {
          headers: { "Authorization": `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch folders");
        
        const data = await res.json();
        setFolders(data);
        setCurrentFolder(null);
        setBreadcrumbs([]);
        setTasks([]);
        
        // Очищаем сохраненную папку
        try {
          localStorage.removeItem('currentFolderId');
        } catch (err) {
          console.error("Failed to remove folder:", err);
        }
      }
    } catch (err) {
      console.error("Fetch folders error:", err);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);
  
  useEffect(() => {
    // При монтировании пытаемся восстановить сохраненную папку
    if (savedFolderId) {
      fetchFolders(Number(savedFolderId));
    } else {
      fetchFolders();
    }
  }, [fetchFolders]);
  
  const handleCreateFolder = async (name, parentId) => {
    const token = getCookie("authToken");
    const res = await fetch(`${API_BASE}/folders/`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name, parent_id: parentId }),
    });
    
    if (res.ok) {
      await fetchFolders(currentFolder?.id || null);
    }
  };
  
  const handleRenameFolder = async (name) => {
    if (!selectedFolder) return;
    
    const token = getCookie("authToken");
    const res = await fetch(`${API_BASE}/folders/${selectedFolder.id}/`, {
      method: "PATCH",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name }),
    });
    
    if (res.ok) {
      await fetchFolders(currentFolder?.id || null);
    }
  };
  
  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm(t.deleteFolderConfirm)) return;
    
    const token = getCookie("authToken");
    const res = await fetch(`${API_BASE}/folders/${folderId}/`, {
      method: "DELETE",
      headers: { "Authorization": `Bearer ${token}` },
    });
    
    if (res.ok) {
      // Если удалили текущую папку, переходим на уровень выше
      if (currentFolder?.id === folderId) {
        const parentFolder = breadcrumbs[breadcrumbs.length - 2];
        if (parentFolder) {
          await fetchFolders(parentFolder.id);
        } else {
          await fetchFolders(null);
        }
      } else {
        await fetchFolders(currentFolder?.id || null);
      }
    }
  };
  
  const handleDuplicateFolder = async (folderId) => {
    try {
      const token = getCookie("authToken");
      const res = await fetch(`${API_BASE}/folders/${folderId}/duplicate/`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
      });
      
      if (res.ok) {
        await fetchFolders(currentFolder?.id || null);
      }
    } catch (err) {
      console.error("Duplicate folder error:", err);
    }
  };
  
  const handleStart = async (taskId) => {
    try {
      const token = getCookie("authToken");
      const res = await fetch(`${API_BASE}/tasks/${taskId}/start/`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (res.ok) fetchFolders(currentFolder?.id || null);
    } catch (err) { console.error(err); }
  };
  
  const handleStop = async (taskId) => {
    try {
      const token = getCookie("authToken");
      const res = await fetch(`${API_BASE}/tasks/${taskId}/pause/`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (res.ok) fetchFolders(currentFolder?.id || null);
    } catch (err) { console.error(err); }
  };
  
  const handleDelete = async (taskId) => {
    if (!window.confirm(t.deleteTaskConfirm)) return;
    
    try {
      const token = getCookie("authToken");
      const res = await fetch(`${API_BASE}/tasks/${taskId}/`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (res.ok) fetchFolders(currentFolder?.id || null);
    } catch (err) { console.error(err); }
  };
  
  const handleDuplicate = (task) => {
    if (onAdd) {
      onAdd({ prefillTask: task });
    }
  };
  
  const handleCreateFilter = (task) => {
    if (onAdd) {
      onAdd({ prefillTask: task, createFilter: true });
    }
  };
  
  const handleOpenRename = (task) => {
  setRenameModal({ 
    open: true, 
    taskId: task.task_id || task.id, 
    currentName: task.task_name || task.name 
  });
  setNewName(task.task_name || task.name);
};

const handleRename = async () => {
  if (!newName.trim() || newName.trim() === renameModal.currentName) {
    setRenameModal({ open: false, taskId: null, currentName: "" });
    return;
  }
  
  try {
    setRenaming(true);
    const token = getCookie("authToken");
    const res = await fetch(`${API_BASE}/tasks/${renameModal.taskId}/rename/`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name: newName.trim() }),
    });
    
    if (!res.ok) throw new Error("Failed to rename");
    
    setRenameModal({ open: false, taskId: null, currentName: "" });
    fetchFolders(currentFolder?.id || null);
  } catch (err) {
    console.error("Rename error:", err);
  } finally {
    setRenaming(false);
  }
};
  
  const handleRemoveFromFolder = async (task) => {
    const token = getCookie("authToken");
    const taskId = task.task_id || task.id;
    
    const res = await fetch(`${API_BASE}/tasks/${taskId}/move-to-folder/`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ folder_id: null }),
    });
    
    if (res.ok) {
      await fetchFolders(currentFolder?.id || null);
    }
  };
  
  const handleRemoveClick = (task) => {
    if (window.confirm(t.removeFromFolderConfirm)) {
      handleRemoveFromFolder(task);
    }
  };
  
  const navigateToFolder = (folderId) => {
    fetchFolders(folderId);
  };
  
  const navigateToBreadcrumb = (index) => {
    if (index === -1) {
      fetchFolders(null);
    } else {
      const folder = breadcrumbs[index];
      if (folder) fetchFolders(folder.id);
    }
  };
  
  return (
    <div>
      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 20,
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? 12 : 0,
      }}>
        <div>
          <h2 style={{ fontSize: 18, margin: 0 }}>{t.folders}</h2>
          <p style={{ fontSize: 13, margin: '4px 0 0 0', color: 'var(--muted)' }}>
            {t.organizeTasks}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {currentFolder && (
            <button
              onClick={() => setShowAddTasksModal(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 16px",
                borderRadius: 10,
                border: "1.5px solid var(--blue)",
                background: "#fff",
                color: "var(--blue)",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              <HiPlus size={18} />
              {t.addTasks}
            </button>
          )}
          
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 16px",
              borderRadius: 10,
              border: "none",
              background: "var(--blue)",
              color: "#fff",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            <HiFolderAdd size={18} />
            {t.newFolder}
          </button>
        </div>
      </div>
      
      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          marginBottom: 16,
          padding: "8px 12px",
          background: "#f8fafc",
          borderRadius: 8,
          fontSize: 13,
          flexWrap: "wrap",
        }}>
          <button
            onClick={() => navigateToBreadcrumb(-1)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--blue)",
              fontWeight: 600,
              padding: 0,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <HiFolderOpen size={16} />
            {t.root}
          </button>
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.id}>
              <HiChevronRight size={14} style={{ color: "var(--muted)" }} />
              <button
                onClick={() => navigateToBreadcrumb(index)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: index === breadcrumbs.length - 1 ? "var(--dark)" : "var(--blue)",
                  fontWeight: index === breadcrumbs.length - 1 ? 700 : 500,
                  padding: 0,
                }}
              >
                {crumb.name}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
      
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <div className="spinner" />
        </div>
      ) : (
        <>
          {/* Folders Grid */}
          {folders.length > 0 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(auto-fill, minmax(200px, 1fr))",
              gap: 12,
              marginBottom: 24,
            }}>
              {folders.map(folder => (
                <div
                  key={folder.id}
                  style={{
                    background: "#fff",
                    borderRadius: 12,
                    border: "1.5px solid var(--border)",
                    padding: "16px",
                    cursor: "pointer",
                    transition: ".15s",
                    position: "relative",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "var(--blue)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(59,113,254,.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "var(--border)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div 
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}
                    onClick={() => navigateToFolder(folder.id)}
                  >
                    <div style={{ flex: 1 }}>
                      <HiFolder size={32} color="#f59e0b" />
                      <div style={{ marginTop: 8, fontWeight: 600, fontSize: 14, color: "var(--dark)" }}>
                        {folder.name}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                        {folder.tasks_count || 0} {t.tasks} · {folder.subfolders_count || 0} {t.subfolders}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ 
                    position: "absolute", top: 12, right: 12,
                    display: "flex", gap: 4,
                  }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicateFolder(folder.id);
                      }}
                      title={t.duplicateFolder}
                      style={{
                        padding: 4,
                        borderRadius: 6,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        color: "var(--muted)",
                        display: "flex",
                        alignItems: "center",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "var(--blue)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "var(--muted)"; }}
                    >
                      <HiDuplicate size={14} />
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFolder(folder);
                        setShowRenameModal(true);
                      }}
                      style={{
                        padding: 4,
                        borderRadius: 6,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        color: "var(--muted)",
                        display: "flex",
                        alignItems: "center",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "var(--blue)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "var(--muted)"; }}
                    >
                      <HiPencil size={14} />
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder.id);
                      }}
                      style={{
                        padding: 4,
                        borderRadius: 6,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        color: "var(--muted)",
                        display: "flex",
                        alignItems: "center",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#dc2626"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "var(--muted)"; }}
                    >
                      <HiTrash size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Tasks in current folder */}
          {tasks.length > 0 && (
            <div>
              <h3 style={{ fontSize: 16, marginBottom: 12 }}>
                {t.tasksInFolder}
              </h3>
              <div className="rules-grid" style={{ marginBottom: 20 }}>
                {tasks.map(task => (
                  <div key={task.task_id || task.id} style={{ position: "relative" }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveClick(task);
                      }}
                      title={t.removeFromFolder}
                      style={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        zIndex: 10,
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        border: "2px solid #fff",
                        background: "#fef2f2",
                        color: "#dc2626",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        transition: "all .15s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#dc2626";
                        e.currentTarget.style.color = "#fff";
                        e.currentTarget.style.transform = "scale(1.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#fef2f2";
                        e.currentTarget.style.color = "#dc2626";
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      <HiX size={16} />
                    </button>
                    
                    <TaskCard 
                      task={task}
                      t={tasksT}
                      onStart={handleStart}
                      onStop={handleStop}
                      onDelete={handleDelete}
                      onVisualize={onVisualize}
                      onEdit={handleOpenRename} 
                      onDuplicate={handleDuplicate}
                      onCreateFilter={handleCreateFilter}
                      onForceStopTopUp={() => {}}
                      userInfo={effectiveUserInfo}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Пустая папка */}
          {currentFolder && tasks.length === 0 && folders.length === 0 && (
            <div style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "var(--muted)",
            }}>
              <HiFolderOpen size={48} style={{ color: "#d1d5db", marginBottom: 16 }} />
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: "var(--dark)" }}>
                {t.folderEmpty}
              </div>
              <div style={{ fontSize: 13, marginBottom: 16 }}>
                {t.addTasksToFolder}
              </div>
              <button
                onClick={() => setShowAddTasksModal(true)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 20px",
                  borderRadius: 10,
                  border: "none",
                  background: "var(--blue)",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                <HiPlus size={18} />
                {t.addTasks}
              </button>
            </div>
          )}
          
          {/* Нет папок */}
          {!currentFolder && folders.length === 0 && (
            <div style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "var(--muted)",
            }}>
              <HiFolder size={48} style={{ color: "#d1d5db", marginBottom: 16 }} />
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: "var(--dark)" }}>
                {t.noFolders}
              </div>
              <div style={{ fontSize: 13 }}>
                {t.createFirstFolder}
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Modals */}
      {showCreateModal && (
        <FolderModal
          mode="create"
          parentId={currentFolder?.id || null}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateFolder}
          isMobile={isMobile}
          t={t}
        />
      )}
      
      {showRenameModal && selectedFolder && (
        <FolderModal
          mode="rename"
          folder={selectedFolder}
          parentId={selectedFolder.parent}
          onClose={() => setShowRenameModal(false)}
          onSave={handleRenameFolder}
          isMobile={isMobile}
          t={t}
        />
      )}
      
      {showAddTasksModal && currentFolder && (
        <AddTasksToFolderModal
          folderId={currentFolder.id}
          folderName={currentFolder.name}
          onClose={() => setShowAddTasksModal(false)}
          onAdded={() => fetchFolders(currentFolder?.id || null)}
          isMobile={isMobile}
          t={t}
        />
      )}



      {/* RENAME MODAL */}
{renameModal.open && (
  <div style={{
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: 16
  }}>
    <div style={{
      background: "#fff", borderRadius: 16, width: "100%",
      maxWidth: 420, padding: 24,
      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    }}>
      <h3 style={{ margin: "0 0 8px 0", fontSize: 18, fontWeight: 700 }}>
        {tasksT.rename?.renameTask || "Rename Task"}
      </h3>
      <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 16 }}>
        {tasksT.rename?.renameTaskDesc || "Enter new name for this task"}
      </p>
      
      <input
        type="text"
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleRename();
          if (e.key === "Escape") setRenameModal({ open: false, taskId: null, currentName: "" });
        }}
        autoFocus
        style={{
          width: "100%", padding: "12px 14px",
          borderRadius: 10, border: "1.5px solid var(--border)",
          fontSize: 14, outline: "none",
          boxSizing: "border-box",
        }}
        placeholder={tasksT.rename?.renamePlaceholder || "Enter task name..."}
      />
      
      <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
        <button
          onClick={() => setRenameModal({ open: false, taskId: null, currentName: "" })}
          style={{
            padding: "8px 16px", borderRadius: 8,
            border: "1.5px solid var(--border)", background: "#fff",
            cursor: "pointer", fontSize: 14, fontWeight: 500,
          }}
        >
          {tasksT.rename?.cancel || "Cancel"}
        </button>
        <button
          onClick={handleRename}
          disabled={renaming || !newName.trim()}
          style={{
            padding: "8px 16px", borderRadius: 8,
            border: "none", background: "var(--blue)",
            color: "#fff", cursor: renaming ? "not-allowed" : "pointer",
            fontSize: 14, fontWeight: 600, opacity: renaming ? 0.6 : 1,
          }}
        >
          {renaming ? (tasksT.rename?.saving || "Saving...") : (tasksT.rename?.save || "Save")}
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
}